import React from "react";
import {
  ArrowRight,
  ChevronDown } from
"lucide-react";
import { heroData } from "../data/mock";

export const HeroSection = () => {
  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0A]">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -right-40 w-[700px] h-[700px] bg-[#F76B1C]/[0.03] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/3 -left-40 w-[500px] h-[500px] bg-[#F76B1C]/[0.02] rounded-full blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "72px 72px"
            }} />

        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#F76B1C]/[0.08] border border-[#F76B1C]/20">
                <span className="w-2 h-2 rounded-full bg-[#F76B1C] animate-pulse" />
                <span className="text-[#F76B1C] text-sm font-semibold tracking-wide">
                  {heroData.badge}
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-extrabold text-white leading-[1.08] tracking-tight">
                {heroData.headline}
              </h1>

              <p className="text-lg text-white/50 leading-relaxed max-w-xl">
                {heroData.subheadline}
              </p>

              {/* CTAs */}
              <div className="pt-2 space-y-3">
                <a
                  href={heroData.primaryCta.href}
                  onClick={(e) => scrollTo(e, heroData.primaryCta.href)}
                  className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-colors duration-200 hover:shadow-xl hover:shadow-[#F76B1C]/25 group text-base">

                  {heroData.primaryCta.label}
                  <ArrowRight
                    size={18}
                    className="group-hover:translate-x-1 transition-transform duration-200" />

                </a>
                {heroData.primaryCtaSub &&
                <p className="max-w-lg !text-sm !mt-[20px] !mb-[10px] text-white/30">
                    {heroData.primaryCtaSub}
                  </p>
                }
                <a
                  href={heroData.secondaryCta.href}
                  onClick={(e) => scrollTo(e, heroData.secondaryCta.href)}
                  className="inline-flex items-center text-sm text-white/40 hover:text-white/70 transition-colors duration-200 underline underline-offset-4 decoration-white/15 hover:decoration-white/30">

                  {heroData.secondaryCta.label}
                </a>
              </div>
            </div>

            {/* Animated printer showcase */}
            <div
              className="relative flex items-center justify-center animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}>

              {/* Pulsing glow */}
              <div className="absolute w-[380px] h-[380px] lg:w-[450px] lg:h-[450px] bg-[#F76B1C]/[0.05] rounded-full animate-pulse-glow" />

              {/* Orbit ring */}
              <div className="absolute w-[420px] h-[420px] lg:w-[500px] lg:h-[500px] rounded-full border border-[#F76B1C]/[0.07] animate-orbit" />

              {/* Orbiting dots */}
              <div className="absolute w-[420px] h-[420px] lg:w-[500px] lg:h-[500px] animate-orbit">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#F76B1C]/50 shadow-[0_0_12px_rgba(247,107,28,0.4)]" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-[#F76B1C]/30" />
              </div>

              {/* Reverse orbit ring */}
              <div className="absolute w-[340px] h-[340px] lg:w-[400px] lg:h-[400px] rounded-full border border-white/[0.03] animate-orbit-reverse" />

              {/* Floating spec labels */}
              <div className="absolute top-4 -left-2 lg:top-6 lg:left-0 z-20 px-3 py-1.5 bg-[#141414]/80 backdrop-blur-xl border border-white/[0.08] rounded-full text-xs text-white/70 font-medium animate-spec-float shadow-lg">
                50µm XY Resolution
              </div>
              <div className="absolute top-2 -right-2 lg:top-4 lg:right-0 z-20 px-3 py-1.5 bg-[#141414]/80 backdrop-blur-xl border border-[#F76B1C]/20 rounded-full text-xs text-[#F76B1C]/80 font-semibold animate-spec-float shadow-lg" style={{ animationDelay: "1.2s" }}>
                100mm/hr Speed
              </div>
              <div className="absolute bottom-20 -left-4 lg:bottom-24 lg:-left-2 z-20 px-3 py-1.5 bg-[#141414]/80 backdrop-blur-xl border border-white/[0.08] rounded-full text-xs text-white/70 font-medium animate-spec-float shadow-lg" style={{ animationDelay: "2.4s" }}>
                98.7% Success
              </div>
              <div className="absolute bottom-12 -right-4 lg:bottom-16 lg:right-0 z-20 px-3 py-1.5 bg-[#141414]/80 backdrop-blur-xl border border-[#F76B1C]/20 rounded-full text-xs text-[#F76B1C]/80 font-semibold animate-spec-float shadow-lg" style={{ animationDelay: "0.6s" }}>
                MSLA Engine
              </div>

              {/* Printer image */}
              <img
                src={heroData.heroImage}
                alt="Formlabs Form 4 3D Printer"
                className="relative z-10 w-full max-w-[400px] lg:max-w-[460px] drop-shadow-[0_20px_60px_rgba(247,107,28,0.12)] animate-float" />

            </div>
          </div>

          {/* Stats bar */}
          <div
            className="mt-20 lg:mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 animate-fade-in-up"
            style={{ animationDelay: "0.4s" }}>

            {heroData.stats.map((stat, i) =>
            <div
              key={i}
              className="p-5 lg:p-6 rounded-2xl bg-white/[0.025] border border-white/[0.05] backdrop-blur-sm hover:border-[#F76B1C]/20 hover:bg-white/[0.04] transition-all duration-300">

                <div className="text-3xl sm:text-4xl font-extrabold text-[#F76B1C] mb-2">
                  {stat.value}
                </div>
                <div className="text-xs text-white/40 leading-relaxed">
                  {stat.label}
                </div>
              </div>
            )}
          </div>

          {/* Scroll indicator */}
          <div className="hidden lg:flex justify-center mt-16">
            <a
              href="#why-formlabs"
              onClick={(e) => scrollTo(e, "#why-formlabs")}
              className="text-white/20 hover:text-white/40 transition-colors duration-300 animate-bounce-slow">

              <ChevronDown size={28} />
            </a>
          </div>
        </div>
      </section>

    </>);

};